/**
 * MCP 연결 코드 발급 경계.
 *
 * Owns:
 * - Overlord `POST /v1/auth/otp` 요청(소비처 `spnp`)과 응답 모양 확인.
 * - 이 요청에 지금 세션의 Bearer 를 명시해 붙이는 일. `authClient` 는 Bearer 를 자동으로 붙이지 않는다.
 * - 서버의 유효기간(`expires_in`)을 이 브라우저의 시계로 본 만료 시각으로 옮기는 일.
 *
 * Does not own:
 * - 코드의 표시 형식과 설치 명령 — `feature/connectCommands`.
 * - 코드를 토큰으로 바꾸는 교환. 설치 스크립트가 Overlord 에 직접 한다. FE 는 토큰을 받지 않는다.
 * - 오류 문구 — `feature/connectCodeError`.
 */
import {
  authClient,
  IntakeContractError,
  readInteger,
  readRecord,
  readString,
  sessionBearerHeader,
} from "@/shared/http";

/** Overlord 가 코드를 묶는 소비처. 게이트웨이의 설치 스크립트가 같은 값으로 교환한다. */
const CONNECT_CODE_CONSUMER = "spnp";
const RESOURCE = "connect code";
// 코드는 다른 머신의 셸 명령에 따옴표 없이 들어간다. 그래서 영숫자 대문자 10자만 받는다.
const CONNECT_CODE_FORMAT = /^[0-9A-Z]{10}$/;

export type ConnectCode = {
  /** 구분자 없는 10자 코드. 영숫자 대문자만 가진다. */
  code: string;
  /**
   * 이 브라우저의 시계로 본 만료 시각. 요청을 보낸 시각에 서버의 유효기간(`expires_in`)을 더한 값이다.
   * 서버의 `expires_at` 은 쓰지 않는다. 브라우저의 시계가 서버와 다르면 그 값으로는 남은 시간이 틀린다.
   */
  expiresAtMs: number;
};

/** 로그인한 세션 없이 발급을 요청했다. 화면은 다시 로그인하라고 안내한다. */
export class MissingSessionError extends Error {
  constructor() {
    super("No signed-in session for connect code issue.");
    this.name = "MissingSessionError";
  }
}

export async function issueConnectCode(): Promise<ConnectCode> {
  const headers = sessionBearerHeader();
  if (headers === null) {
    throw new MissingSessionError();
  }
  // 요청을 보낸 시각을 기준으로 삼는다. 그러면 화면의 만료 시각이 서버의 만료 시각보다 늦지 않다.
  const requestedAtMs = Date.now();
  const response = await authClient.post<unknown>(
    "/v1/auth/otp",
    { consumer: CONNECT_CODE_CONSUMER },
    { headers },
  );
  const body = readRecord(response.data, RESOURCE, "body");
  return {
    code: readConnectCode(body.code),
    expiresAtMs: requestedAtMs + readLifetimeSeconds(body.expires_in) * 1000,
  };
}

function readLifetimeSeconds(value: unknown): number {
  const seconds = readInteger(value, RESOURCE, "expires_in");
  if (seconds <= 0) {
    throw new IntakeContractError(RESOURCE, "expires_in");
  }
  return seconds;
}

function readConnectCode(value: unknown): string {
  const code = readString(value, RESOURCE, "code");
  if (!CONNECT_CODE_FORMAT.test(code)) {
    throw new IntakeContractError(RESOURCE, "code");
  }
  return code;
}
